/**
 * Task Suggestion List
 *
 * Priority-sorted table of task suggestions with pagination and a detail panel.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Rocket,
  Package,
  Settings,
  TestTube,
  Shield,
  FileText,
  Zap,
  AlertTriangle,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { logger } from '../../utils/logger';
import { formatAgo } from '../../utils/relativeTime';
import { ConfirmModal } from '../ConfirmModal';
import type { TaskSuggestion } from './TaskSuggestionCard';
import { TaskDetailPanel } from './TaskDetailPanel';

interface NoticeState {
  title: string;
  message: string;
  displayName?: string;
  existingTaskId?: string;
  startedAt?: string;
  lastActivityAt?: string;
}

const iconMap: Record<string, typeof Zap> = {
  rocket: Rocket,
  package: Package,
  settings: Settings,
  test: TestTube,
  shield: Shield,
  file: FileText,
  filetext: FileText,
  zap: Zap,
};

type Priority = 'critical' | 'high' | 'medium' | 'low';

const PRIORITY_ORDER_MAP: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

const PRIORITY_CONFIG: Record<Priority, { label: string; badgeStyle: string }> = {
  critical: {
    label: 'Critical',
    badgeStyle: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
  high: {
    label: 'High',
    badgeStyle: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  },
  medium: {
    label: 'Medium',
    badgeStyle: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  low: {
    label: 'Low',
    badgeStyle: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  },
};

const PAGE_SIZE = 10;

function getIconComponent(suggestion: TaskSuggestion): typeof Zap {
  const priority = suggestion.priority || 'medium';
  if (priority === 'critical') return AlertTriangle;
  if (priority === 'low') return Info;
  return (suggestion.icon && iconMap[suggestion.icon]) || Zap;
}

interface TaskSuggestionListProps {
  suggestions: TaskSuggestion[];
  repositoryId: string;
  containerId?: string;
}

export function TaskSuggestionList({
  suggestions,
  repositoryId,
  containerId,
}: TaskSuggestionListProps) {
  const navigate = useNavigate();
  const api = useAuthenticatedApi();
  const [startingTask, setStartingTask] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<TaskSuggestion | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const sorted = useMemo(
    () =>
      [...suggestions].sort((a, b) => {
        const pa = PRIORITY_ORDER_MAP[(a.priority || 'medium') as Priority];
        const pb = PRIORITY_ORDER_MAP[(b.priority || 'medium') as Priority];
        if (pa !== pb) return pa - pb;
        return a.displayName.localeCompare(b.displayName);
      }),
    [suggestions],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, sorted.length);

  const counts = useMemo(() => {
    const c: Record<Priority, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const s of suggestions) c[(s.priority || 'medium') as Priority]++;
    return c;
  }, [suggestions]);

  const handleStartTask = useCallback(
    async (taskClass: string) => {
      setStartingTask(taskClass);
      try {
        const response = await api.post('/tasks/start', {
          taskClass,
          repositoryId,
          containerId,
        });
        navigate(`/tasks/${response.data.taskId}`);
      } catch (error: unknown) {
        logger.error('Failed to start task:', { error });
        const axiosErr = error as {
          response?: {
            status?: number;
            data?: {
              error?: string;
              message?: string;
              displayName?: string;
              existingTaskId?: string;
              startedAt?: string;
              lastActivityAt?: string;
            };
          };
          message?: string;
        };
        const data = axiosErr.response?.data;
        const message = data?.message || data?.error || axiosErr.message || 'Failed to start task.';
        const isAlreadyRunning = axiosErr.response?.status === 409 && !!data?.existingTaskId;
        setNotice({
          title: isAlreadyRunning ? 'Task already running' : 'Unable to start task',
          message,
          displayName: data?.displayName,
          existingTaskId: isAlreadyRunning ? data?.existingTaskId : undefined,
          startedAt: isAlreadyRunning ? data?.startedAt : undefined,
          lastActivityAt: isAlreadyRunning ? data?.lastActivityAt : undefined,
        });
        setStartingTask(null);
      }
    },
    [api, repositoryId, containerId, navigate],
  );

  const handleCancelRunning = useCallback(async () => {
    if (!cancelTargetId) return;
    try {
      await api.post(`/tasks/${cancelTargetId}/cancel`);
    } catch (error: unknown) {
      logger.error('Failed to cancel running task:', { error });
    } finally {
      setCancelTargetId(null);
    }
  }, [api, cancelTargetId]);

  const noticeMessage = useMemo(() => {
    if (!notice) return '';
    if (!notice.startedAt) return notice.message;
    let timing = `Started ${formatAgo(notice.startedAt)}`;
    if (notice.lastActivityAt) timing += `, last activity ${formatAgo(notice.lastActivityAt)}`;
    return `${notice.message} ${timing}.`;
  }, [notice]);

  return (
    <>
      <div className="space-y-4">
        {/* Summary bar */}
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-gray-900 dark:text-white">
            {suggestions.length} recommended {suggestions.length === 1 ? 'action' : 'actions'}
          </span>
          {counts.critical > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
              {counts.critical} critical
            </span>
          )}
          {counts.high > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {counts.high} high
            </span>
          )}
          {counts.medium > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{counts.medium} medium</span>
          )}
          {counts.low > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {counts.low} suggestions
            </span>
          )}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-10">
                  #
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Task
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28">
                  Priority
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-28">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {paged.map((suggestion, index) => {
                const priority = (suggestion.priority || 'medium') as Priority;
                const config = PRIORITY_CONFIG[priority];
                const IconComponent = getIconComponent(suggestion);
                const isStarting = startingTask === suggestion.taskClass;

                return (
                  <tr
                    key={suggestion.taskClass}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                      {rangeStart + index}
                    </td>

                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setSelectedSuggestion(suggestion)}
                        className="group inline-flex items-center gap-2 cursor-pointer text-left"
                      >
                        <IconComponent
                          className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors"
                          strokeWidth={1.5}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 group-hover:underline transition-colors">
                          {suggestion.displayName}
                        </span>
                      </button>
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${config.badgeStyle}`}
                      >
                        {config.label}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartTask(suggestion.taskClass);
                        }}
                        disabled={isStarting}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        {isStarting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {isStarting ? 'Starting...' : priority === 'critical' ? 'Fix Now' : 'Run'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Row count + Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Showing {rangeStart}–{rangeEnd} of {sorted.length}{' '}
              {sorted.length === 1 ? 'action' : 'actions'}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <TaskDetailPanel
        suggestion={selectedSuggestion}
        isRunning={startingTask === selectedSuggestion?.taskClass}
        onClose={() => setSelectedSuggestion(null)}
        onRun={handleStartTask}
      />

      <ConfirmModal
        isOpen={notice !== null}
        onClose={() => setNotice(null)}
        onConfirm={() => {
          if (notice?.existingTaskId) navigate(`/tasks/${notice.existingTaskId}`);
          setNotice(null);
        }}
        title={notice?.title ?? 'Unable to start task'}
        message={noticeMessage}
        variant="warning"
        size="xl"
        confirmText={notice?.existingTaskId ? 'View running task' : 'OK'}
        cancelText="Close"
        tertiaryText={notice?.existingTaskId ? 'Cancel running task' : undefined}
        tertiaryVariant="danger"
        onTertiary={() => {
          const id = notice?.existingTaskId;
          setNotice(null);
          if (id) setCancelTargetId(id);
        }}
      />

      <ConfirmModal
        isOpen={cancelTargetId !== null}
        onClose={() => setCancelTargetId(null)}
        onConfirm={handleCancelRunning}
        title="Cancel running task"
        message="This stops the in-flight run and discards any partial changes in its sandbox. You can start a fresh run afterward. Continue?"
        variant="danger"
        confirmText="Cancel running task"
        cancelText="Keep running"
      />
    </>
  );
}
